import { z } from 'zod';

/**
 * Cart Item Schema
 * Handles individual line items with robust fallbacks.
 */
export const CartItemSchema = z.object({
    id: z.number(),
    // Key usually exists, but fallback to ID string if missing
    key: z.string().optional().transform((val, ctx: any) => val || String(ctx?.path?.[ctx?.path?.length - 1] || 'unknown')),
    product_title: z.string().default('Unknown Product'),
    variant_title: z.string().nullable().default(null),
    vendor: z.string().default(''),
    quantity: z.number().int().default(0),
    price: z.number().default(0),
    original_price: z.number().optional().default(0),
    final_line_price: z.number().default(0),
    original_line_price: z.number().default(0),
    line_level_total_discount: z.number().default(0),
    // Image handling: Try 'image' field, fallback to 'featured_image.url', fallback to empty string
    image: z.string().nullish().or(z.literal(false)).transform(val => val || ''),
    featured_image: z.object({ url: z.string() }).optional(),

    url: z.string().default(''),
    inventory_quantity: z.number().optional(),
    selling_plan_allocation: z
        .object({
            selling_plan_id: z.number()
        })
        .optional(),
    // Use preprocess to handle "empty array means undefined" before validation
    properties: z.preprocess(
        (val) => (Array.isArray(val) && val.length === 0 ? undefined : val),
        // Force strings relative to Zod record: keys are string by default, value must be string
        z.record(z.string(), z.string()).optional()
    ),
}).transform(item => ({
    ...item,
    // Normalize logic
    key: item.key || String(item.id),
    original_price: item.original_price || item.price,
    // Image fallback logic if 'image' was null but 'featured_image' exists
    image: (item.image || item.featured_image?.url || '') as string
}));

/**
 * Discount Application Schema
 */
export const DiscountApplicationSchema = z.object({
    type: z.string().nullable().optional().default(''),
    title: z.string().default(''),
    total_allocated_amount: z.number().default(0)
});

export type DiscountApplication = z.infer<typeof DiscountApplicationSchema>;

const DiscountCodeSchema = z.object({
    code: z.string().default(''),
    applicable: z.boolean().default(false)
});

/**
 * Cart Response Schema
 * Validates the full /cart.js response
 */
export const CartResponseSchema = z.object({
    item_count: z.number().default(0),
    total_price: z.number().default(0),
    original_total_price: z.number().default(0),
    total_discount: z.number().default(0),
    currency: z.string().default('EUR'),
    items: z.array(CartItemSchema).default([]),
    cart_level_discount_applications: z.array(DiscountApplicationSchema).default([]),
    discount_codes: z.array(DiscountCodeSchema).default([])
});

export type CartResponse = z.infer<typeof CartResponseSchema>;
export type CartItem = z.infer<typeof CartItemSchema>;
